import React, { useEffect, useRef, useState } from "react";

/**
 * Cursor-following voltage glow for dark hero sections.
 * - Disabled on touch devices and when prefers-reduced-motion.
 * - 200ms lag for smoothness.
 */
export default function MouseGlow({ color = "rgba(212,255,63,0.18)", size = 600, className = "" }) {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(hover: none)").matches;
    setEnabled(!(reduced || isTouch));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    let raf;
    let tx = -9999, ty = -9999;
    let cx = -9999, cy = -9999;
    const onMove = (e) => {
      const rect = el.parentElement.getBoundingClientRect();
      tx = e.clientX - rect.left;
      ty = e.clientY - rect.top;
    };
    const tick = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      el.style.transform = `translate3d(${cx - size / 2}px, ${cy - size / 2}px, 0)`;
      raf = requestAnimationFrame(tick);
    };
    el.parentElement.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.parentElement?.removeEventListener("mousemove", onMove);
    };
  }, [enabled, size]);

  if (!enabled) return null;

  return (
    <div
      ref={ref}
      aria-hidden
      className={`pointer-events-none absolute z-0 rounded-full blur-3xl will-change-transform ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 60%)`,
      }}
    />
  );
}
